import { CHART_COLORS } from '@/constants/colors'
import { BENCHMARK_SELECTED_BENCHMARKS_COOKIE_KEY, BENCHMARK_SELECTED_VAULTS_COOKIE_KEY } from '@/constants/cookie'
import { ANALYTICS_PAGE_KEYS } from '@/constants/state'
import { useAccountContext } from '@/context/account'
import { useSearchState } from '@/hooks/useSearchState'
import { useHistoricalApyQueryOptions } from '@/queries/historicalApy.query'
import type { HistoricalBenchmark } from '@/queries/historicalBenchmarks.query'
import { usePositionsQuery } from '@/queries/positions.query'
import { useVaultsQuery } from '@/queries/vaults.query'
import type { VaultApy, VaultApyRange } from '@/types/apy'
import type { ChartPeriod } from '@/types/periods'
import type { Vault } from '@/types/vault'
import { mergeHistoricalData } from '@/utils/chartUtils/interpolateApyBenchmarkData'
import { setCookieValue } from '@/utils/cookie/cookie'
import { DAY_IN_SECONDS, SECOND_IN_MILLISECONDS, YEAR_IN_DAYS } from '@vaultsfyi/common/constants'
import type { ApyBreakdown, ApyIntervalBreakdown } from '@vaultsfyi/common/schemas'
import { useCallback, useMemo } from 'react'
import type { BenchmarkItem } from '../../components/analytics/vaultSelector'

type BenchmarkKey = 'eth-mainnet' | 'usd-mainnet'

const ETH_BENCHMARK_KEY: BenchmarkKey = 'eth-mainnet'
const USD_BENCHMARK_KEY: BenchmarkKey = 'usd-mainnet'
const ETH_BENCHMARK_NAME = 'vaults.fyi ETH Benchmark'
const USD_BENCHMARK_NAME = 'vaults.fyi USD Benchmark'

const BENCHMARK_COLORS: Record<BenchmarkKey, string> = {
  [ETH_BENCHMARK_KEY]: `rgb(${CHART_COLORS.blue})`,
  [USD_BENCHMARK_KEY]: `rgb(${CHART_COLORS.green})`,
}

const VAULT_COLORS: readonly string[] = Object.values(CHART_COLORS).map((v) => `rgb(${v})`)

const isBenchmarkKey = (key: string): key is BenchmarkKey => key === ETH_BENCHMARK_KEY || key === USD_BENCHMARK_KEY

const getBenchmarkDisplayName = (key: BenchmarkKey): string =>
  key === ETH_BENCHMARK_KEY ? ETH_BENCHMARK_NAME : USD_BENCHMARK_NAME

export const BENCHMARK_ITEMS: BenchmarkItem[] = [
  { type: 'benchmark', id: ETH_BENCHMARK_KEY, name: ETH_BENCHMARK_NAME, symbol: 'ETH' },
  { type: 'benchmark', id: USD_BENCHMARK_KEY, name: USD_BENCHMARK_NAME, symbol: 'USDC' },
]

// ─── Types ────────────────────────────────────────────────────────────────────

export interface LeaderboardItem {
  key: string
  apy: number
  vault: Vault | undefined
  color: string
  name: string
}

type ChartDataPoint = Record<string, number | undefined> & { timestamp: number }

interface Params {
  apyRange: VaultApyRange
  chartPeriod: ChartPeriod
  includeRewards: boolean
  ethBenchmarkData?: HistoricalBenchmark
  usdBenchmarkData?: HistoricalBenchmark
  selectedVaults: Vault[]
  selectedBenchmarks: BenchmarkItem[]
  onVaultsChange: (vaults: Vault[]) => void
  onBenchmarksChange: (benchmarks: BenchmarkItem[]) => void
}

export function useBenchmarkApyChart({
  apyRange,
  chartPeriod,
  includeRewards,
  ethBenchmarkData,
  usdBenchmarkData,
  selectedVaults,
  selectedBenchmarks,
  onVaultsChange,
  onBenchmarksChange,
}: Params) {
  const { account } = useAccountContext()
  const { data: vaults } = useVaultsQuery({ initialData: undefined })
  const { data: positionsList } = usePositionsQuery({ address: account, apyInterval: apyRange })
  const { updateSearchState } = useSearchState({})

  const handleSetSelectedVaults = useCallback(
    (vaults: Vault[]) => {
      setCookieValue(
        BENCHMARK_SELECTED_VAULTS_COOKIE_KEY,
        vaults.map((v) => ({ network: v.network.name, vaultId: v.vaultId }))
      )
      updateSearchState(
        [ANALYTICS_PAGE_KEYS.benchmarks.selectedVaults, vaults.map((v) => `${v.network.name}:${v.vaultId}`)],
        [ANALYTICS_PAGE_KEYS.benchmarks.selectedBenchmarks, selectedBenchmarks.map((b) => b.id)]
      )
      onVaultsChange(vaults)
    },
    [updateSearchState, selectedBenchmarks, onVaultsChange]
  )

  const handleSetSelectedBenchmarks = useCallback(
    (benchmarks: BenchmarkItem[]) => {
      setCookieValue(
        BENCHMARK_SELECTED_BENCHMARKS_COOKIE_KEY,
        benchmarks.map((b) => b.id)
      )
      updateSearchState(
        [ANALYTICS_PAGE_KEYS.benchmarks.selectedVaults, selectedVaults.map((v) => `${v.network.name}:${v.vaultId}`)],
        [ANALYTICS_PAGE_KEYS.benchmarks.selectedBenchmarks, benchmarks.map((b) => b.id)]
      )
      onBenchmarksChange(benchmarks)
    },
    [updateSearchState, selectedVaults, onBenchmarksChange]
  )

  const benchmarkApyMap = useMemo(() => {
    const map: Partial<Record<string, VaultApy>> = {}
    const ethLast = ethBenchmarkData?.[ethBenchmarkData.length - 1]
    const usdLast = usdBenchmarkData?.[usdBenchmarkData.length - 1]
    if (ethLast?.apy) map[ETH_BENCHMARK_KEY] = ethLast.apy as VaultApy
    if (usdLast?.apy) map[USD_BENCHMARK_KEY] = usdLast.apy as VaultApy
    return map
  }, [ethBenchmarkData, usdBenchmarkData])

  const { data: vaultData, isLoading } = useHistoricalApyQueryOptions({
    queryParams: selectedVaults.map((vault) => ({ vaultId: vault.vaultId, network: vault.network.name })),
    apyRange,
    period: YEAR_IN_DAYS,
  })

  const mergedData = useMemo(
    () =>
      mergeHistoricalData({
        vaultData,
        ethBenchmarkData: selectedBenchmarks.some((b) => b.id === ETH_BENCHMARK_KEY) ? ethBenchmarkData : undefined,
        usdBenchmarkData: selectedBenchmarks.some((b) => b.id === USD_BENCHMARK_KEY) ? usdBenchmarkData : undefined,
      }),
    [vaultData, ethBenchmarkData, usdBenchmarkData, selectedBenchmarks]
  )

  const lastTimestamp = mergedData.length > 0 ? (mergedData[mergedData.length - 1]?.timestamp ?? null) : null

  const {
    chartData,
    dataKeys,
    sortedDataKeys,
    nonBenchmarkKeys,
    keyToName,
    getKeyColor,
    getStrokeWidth,
    leaderboardItems,
  } = useMemo(() => {
    const allDataKeysSet = new Set<string>()
    for (const point of mergedData) {
      for (const key of Object.keys(point)) {
        if (key !== 'timestamp') allDataKeysSet.add(key)
      }
    }
    const dataKeys = Array.from(allDataKeysSet)

    const periodMs = chartPeriod * DAY_IN_SECONDS * SECOND_IN_MILLISECONDS
    const cutoffTimestamp = Math.floor((Date.now() - periodMs) / SECOND_IN_MILLISECONDS)
    const filteredData = mergedData.filter((point) => point.timestamp >= cutoffTimestamp)

    const chartData: ChartDataPoint[] = filteredData.map((point) => {
      const transformed: ChartDataPoint = { timestamp: point.timestamp }
      for (const key of dataKeys) {
        const value = point[key]
        if (typeof value === 'object' && value !== null) {
          if ('base' in value && 'reward' in value && 'total' in value) {
            const breakdown = value as ApyBreakdown
            transformed[key] = includeRewards ? breakdown.total : breakdown.base
          } else if ('1day' in value || '7day' in value || '30day' in value) {
            const intervalBreakdown = value as ApyIntervalBreakdown
            const rangeData = intervalBreakdown[apyRange]
            if (rangeData) transformed[key] = includeRewards ? rangeData.total : rangeData.base
          }
        }
      }
      return transformed
    })

    for (const key of dataKeys) {
      let lastValidIndex = -1
      let lastValidValue: number | undefined
      for (let i = 0; i < chartData.length; i++) {
        const value = chartData[i][key]
        if (value !== undefined && value !== null) {
          if (lastValidIndex !== -1 && i - lastValidIndex > 1) {
            const steps = i - lastValidIndex
            const valueDiff = value - lastValidValue!
            for (let j = lastValidIndex + 1; j < i; j++) {
              chartData[j][key] = lastValidValue! + (valueDiff * (j - lastValidIndex)) / steps
            }
          }
          lastValidIndex = i
          lastValidValue = value
        }
      }
    }

    const keyToName: Record<string, string> = {}
    for (const key of dataKeys) {
      keyToName[key] = isBenchmarkKey(key)
        ? getBenchmarkDisplayName(key)
        : (selectedVaults.find((v) => `${v.vaultId}-${v.network.name}` === key)?.name ?? key)
    }

    const getKeyColor = (key: string, vaultIndex: number): string =>
      isBenchmarkKey(key) ? BENCHMARK_COLORS[key] : (VAULT_COLORS[vaultIndex % VAULT_COLORS.length] ?? 'transparent')

    const getStrokeWidth = (key: string): number => (isBenchmarkKey(key) ? 4 : 2)

    const nonBenchmarkKeys = dataKeys.filter((k) => !isBenchmarkKey(k))

    const sortedDataKeys = [...dataKeys].sort((a, b) => {
      const aIsB = isBenchmarkKey(a)
      const bIsB = isBenchmarkKey(b)
      if (aIsB && !bIsB) return -1
      if (!aIsB && bIsB) return 1
      if (aIsB && bIsB) {
        if (a === ETH_BENCHMARK_KEY) return -1
        if (b === ETH_BENCHMARK_KEY) return 1
      }
      return 0
    })

    const leaderboardItems: LeaderboardItem[] = dataKeys
      .map((key) => {
        const vaultIndex = nonBenchmarkKeys.indexOf(key)
        let apy: number | undefined
        for (let i = mergedData.length - 1; i >= 0; i--) {
          const value = mergedData[i][key]
          if (typeof value === 'object' && value !== null) {
            if ('base' in value && 'reward' in value && 'total' in value) {
              const breakdown = value as ApyBreakdown
              apy = includeRewards ? breakdown.total : breakdown.base
              break
            } else if ('1day' in value || '7day' in value || '30day' in value) {
              const intervalBreakdown = value as ApyIntervalBreakdown
              const rangeData = intervalBreakdown[apyRange]
              if (rangeData) {
                apy = includeRewards ? rangeData.total : rangeData.base
                break
              }
            }
          }
        }
        const vault = selectedVaults.find((v) => `${v.vaultId}-${v.network.name}` === key)
        return { key, apy, vault, color: getKeyColor(key, vaultIndex), name: keyToName[key] }
      })
      .filter((item): item is LeaderboardItem => item.apy !== undefined)
      .sort((a, b) => b.apy - a.apy)

    return {
      chartData,
      dataKeys,
      sortedDataKeys,
      nonBenchmarkKeys,
      keyToName,
      getKeyColor,
      getStrokeWidth,
      leaderboardItems,
    }
  }, [mergedData, apyRange, includeRewards, chartPeriod, selectedVaults])

  return {
    vaults,
    positionsList,
    isLoading,
    benchmarkApyMap,
    chartData,
    dataKeys,
    sortedDataKeys,
    nonBenchmarkKeys,
    keyToName,
    getKeyColor,
    getStrokeWidth,
    leaderboardItems,
    lastTimestamp,
    handleSetSelectedVaults,
    handleSetSelectedBenchmarks,
  }
}
